import { useSyncExternalStore } from 'react';

// =============================================================================
// NOTIFICATION PERMISSION via useSyncExternalStore
// =============================================================================
//
// Extracted verbatim from useWebPushNotifications.ts so the OneSignal opt-in
// adapter (useOneSignalPush) can read the same reactive Notification.permission
// store and drive notifyPermissionChange() after a permission request — without
// pulling in the whole legacy VAPID hook. Behaviour is byte-identical to the
// original inline store; this is a pure move.

const permissionSubscribers = new Set<() => void>();
let cachedPermission: NotificationPermission =
  typeof Notification !== 'undefined' ? Notification.permission : 'default';

export const notifyPermissionChange = () => {
  if (typeof Notification === 'undefined') return;
  cachedPermission = Notification.permission;
  permissionSubscribers.forEach(cb => {
    try {
      cb();
    } catch {
      // Swallow: one bad subscriber must not break the permission store.
    }
  });
};

const subscribeToPermission = (callback: () => void): (() => void) => {
  permissionSubscribers.add(callback);

  let cleaned = false;
  let permStatus: PermissionStatus | null = null;

  // Strategy 1: PermissionStatus.change (Chrome/Firefox/Edge)
  // Feature-detect to avoid rejection on Safari (permissions.query is undefined there)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (navigator.permissions?.query) {
    navigator.permissions
      .query({ name: 'notifications' })
      .then(status => {
        if (cleaned) return; // component unmounted while promise was pending
        permStatus = status;
        permStatus.addEventListener('change', notifyPermissionChange);
        // Sync in case permission changed between render and subscription
        if (Notification.permission !== cachedPermission) {
          notifyPermissionChange();
        }
      })
      .catch(() => {
        // Some browsers may reject the query — visibilitychange fallback covers this
      });
  }

  // Strategy 2: visibilitychange polling (Safari fallback, harmless no-op elsewhere)
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && Notification.permission !== cachedPermission) {
      notifyPermissionChange();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    cleaned = true;
    permissionSubscribers.delete(callback);
    if (permStatus) {
      permStatus.removeEventListener('change', notifyPermissionChange);
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

const getPermissionSnapshot = (): NotificationPermission => cachedPermission;

/**
 * Reactively track `Notification.permission`. Backed by a module-level
 * `useSyncExternalStore` store so multiple consumers (legacy VAPID hook +
 * OneSignal adapter) share one subscription set and one cached snapshot.
 *
 * Call `notifyPermissionChange()` after any programmatic
 * `Notification.requestPermission()` (or SDK equivalent) so subscribers observe
 * the new value immediately rather than waiting for a `visibilitychange` tick.
 */
export const useNotificationPermission = (): NotificationPermission =>
  useSyncExternalStore(
    subscribeToPermission,
    getPermissionSnapshot,
    // SSR snapshot (not applicable but required by the API)
    () => 'default' as NotificationPermission,
  );
